import { createBrowserRouter, RouterProvider, Navigate, Outlet, useLocation } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import HomePage from './pages/public/HomePage.jsx';
import UserProfilePage from './pages/UserProfilePage';
import UserOrdersPage from './pages/UserOrdersPage';
import ProductDetailPage from './pages/public/ProductDetailPage.jsx';
import CartPage from './pages/CartPage.jsx';
import ProtectedRoute from './components/common/ProtectedRoute.jsx';
import AuthPage from './pages/public/AuthPage.jsx';
import ForgotPassword from './pages/public/ForgotPassword.jsx';
import AdminProductCreate from './pages/admin/AdminProductCreate';
import AdminOrderList from './pages/admin/AdminOrderList';
import AdminItemList from './pages/admin/AdminItemList';
import AdminAttributes from './pages/admin/AdminAttributes';
import AdminMenuList from './pages/admin/AdminMenuList';
import AdminContent from './pages/admin/AdminContent';
import AdminProductEdit from './pages/admin/AdminProductEdit';
import MenuPage from './pages/public/MenuPage.jsx';
import NewsPage from './pages/public/NewsPage';
import NewsDetailPage from './pages/public/NewsDetailPage';
import SearchPage from './pages/public/SearchPage';
import AdminSettings from './pages/admin/AdminSettings';
import AdminAccountList from './pages/admin/AdminAccountList';
import UserAddressPage from './pages/UserAddressPage.jsx';
import UserPasswordPage from './pages/UserPasswordPage.jsx';
import AdminNewsCreate from './pages/admin/AdminNewsCreate';
import AdminNewsEdit from './pages/admin/AdminNewsEdit';
import { CartProvider } from './contexts/CartContext';
import CheckoutPage from './pages/CheckoutPage';
import AdminOrderDetail from './pages/admin/AdminOrderDetail';
import FlashSalePage from './pages/public/FlashSalePage.jsx';
import BuildPCPage from './pages/public/BuildPCPage.jsx';
import AdminPcBuilderConfig from './pages/admin/AdminPcBuilderConfig';
import AdminPaymentList from './pages/admin/AdminPaymentList.jsx';
import AdminChatPage from './pages/admin/AdminChatPage';
import HotDealPage from './pages/public/HotDealPage';
import AdminActionLogPage from './pages/admin/AdminActionLogPage';
import catalogService from './services/catalogService';

import AdminLayout from './layouts/AdminLayout';
import Dashboard from './pages/admin/Dashboard';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TitleUpdater from './components/common/TitleUpdater';
import ScrollToTop from './components/common/ScrollToTop.jsx';
import ChatWidget from './components/common/ChatWidget';
import { GoogleOAuthProvider } from '@react-oauth/google';

const RootLayout = () => {
    const location = useLocation();
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    useEffect(() => {
        const checkAuth = () => {
            const token = localStorage.getItem('access_token');
            setIsLoggedIn(!!token);
        };
        checkAuth();
        window.addEventListener('storage', checkAuth);
        return () => window.removeEventListener('storage', checkAuth);
    }, [location.pathname]);

    return (
        <div className="app-container min-h-screen bg-gray-50 flex flex-col">
            <TitleUpdater />
            <ScrollToTop />
            <Outlet />
            {isLoggedIn && !location.pathname.startsWith('/admin') && <ChatWidget />}
        </div>
    );
};

const router = createBrowserRouter([
    {
        element: <RootLayout />,
        children: [
            // ================= LUỒNG KHÁCH (GUEST) =================
            { path: '/', element: <HomePage /> },
            { path: '/san-pham/:slug', element: <ProductDetailPage /> },
            { path: '/login', element: <AuthPage /> },
            { path: '/register', element: <AuthPage /> },
            { path: '/forgot-password', element: <ForgotPassword /> },
            { path: '/cart', element: <CartPage /> },
            { path: '/danh-muc/:slug', element: <MenuPage /> },
            { path: '/tin-tuc', element: <NewsPage /> },
            { path: '/tin-tuc/:slug', element: <NewsDetailPage /> },
            { path: '/search', element: <SearchPage /> },
            { path: '/flash-sale', element: <FlashSalePage /> },
            { path: '/build-pc', element: <BuildPCPage /> },
            { path: '/hot-deal', element: <HotDealPage /> },

            // ================= LUỒNG NGƯỜI DÙNG ĐÃ ĐĂNG NHẬP =================
            {
                element: <ProtectedRoute />,
                children: [
                    { path: '/profile', element: <UserProfilePage /> },
                    { path: '/my-orders', element: <UserOrdersPage /> },
                    { path: '/profile/addresses', element: <UserAddressPage /> },
                    { path: '/profile/password', element: <UserPasswordPage /> },
                    { path: '/checkout', element: <CheckoutPage /> },
                ],
            },

            // ================= LUỒNG QUẢN LÝ & ADMIN =================
            {
                path: '/admin',
                element: <AdminLayout />,
                children: [
                    { index: true, element: <Dashboard /> },
                    { path: 'products', element: <AdminItemList /> },
                    { path: 'products/create', element: <AdminProductCreate /> },
                    { path: 'products/edit/:id', element: <AdminProductEdit /> },
                    { path: 'orders', element: <AdminOrderList /> },
                    { path: 'orders/:id', element: <AdminOrderDetail /> },
                    { path: 'attributes', element: <AdminAttributes /> },
                    { path: 'menus', element: <AdminMenuList /> },
                    { path: 'content', element: <AdminContent /> },
                    { path: 'news/create', element: <AdminNewsCreate /> },
                    { path: 'news/edit/:id', element: <AdminNewsEdit /> },
                    { path: 'settings', element: <AdminSettings /> },
                    { path: 'accounts', element: <AdminAccountList /> },
                    { path: 'pc-builder-config', element: <AdminPcBuilderConfig /> },
                    { path: 'payments', element: <AdminPaymentList /> },
                    { path: 'chat', element: <AdminChatPage /> },
                    { path: 'action-logs', element: <AdminActionLogPage /> },
                ],
            },

            { path: '*', element: <Navigate to="/" /> },
        ],
    },
]);

function App() {
    useEffect(() => {
        const token = localStorage.getItem('access_token');
        let isAdminOrStaff = false;

        if (token) {
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                const isExpired = payload.exp * 1000 < Date.now();

                if (isExpired) {
                    localStorage.removeItem('access_token');
                    localStorage.removeItem('user_info');
                    toast.warn("Phiên đăng nhập đã hết hạn, bạn đã được chuyển về trang chủ.");
                    window.dispatchEvent(new Event('storage'));
                    
                    router.navigate('/');
                } else {
                    const rolesObj = payload.scope || payload.roles || payload.authorities || payload.role || '';
                    const rolesString = typeof rolesObj === 'string' ? rolesObj.toUpperCase() : JSON.stringify(rolesObj).toUpperCase();
                    if (rolesString.includes('ADMIN') || rolesString.includes('STAFF')) {
                        isAdminOrStaff = true;
                    }
                }
            } catch (e) {
                console.error("Token không hợp lệ");
            }
        }

        // ================= THAY ĐỔI Ở ĐÂY =================
        if (!isAdminOrStaff) {
            // Kiểm tra xem session này đã đếm chưa
            const hasCountedVisit = sessionStorage.getItem('has_counted_visit');
            
            if (!hasCountedVisit) {
                // Đợi 3s mới gọi API (Lọc các bot bounce/quét link rồi out ngay lập tức)
                const timer = setTimeout(() => {
                    catalogService.recordVisit()
                        .then(() => {
                            // Đánh dấu là đã đếm thành công
                            sessionStorage.setItem('has_counted_visit', 'true');
                        })
                        .catch(err => console.log("Lỗi traffic:", err));
                }, 3000); 

                // Cleanup timer nếu component unmount nhanh hơn 3s
                return () => clearTimeout(timer);
            }
        }
    }, []);

    return (
        <GoogleOAuthProvider clientId="166755023940-qkh8m5abnu49nlle46t6nika6o9uf18r.apps.googleusercontent.com">
            <CartProvider>
                <RouterProvider router={router} />
                <ToastContainer position="top-right" autoClose={2500} hideProgressBar={false} closeOnClick pauseOnHover theme="colored" />
            </CartProvider>
        </GoogleOAuthProvider>
    );
}

export default App;